import type { CSSProperties } from "react";
import type { Locale } from "@/lib/i18n";
import { RESOURCE_HUB_THEMES, type ResourceHub } from "@/components/resources/ResourceCover";

interface ArticleHeroFrameProps {
  tag?: string | null;
  readingMinutes: number;
  locale: Locale;
  hookOverride?: string | null;
}

function resolveHub(tag: string | null | undefined): ResourceHub {
  const value = (tag ?? "").toLowerCase();
  if (value.includes("performance") || value.includes("rendimiento") || value.includes("speed") || value.includes("velocidad")) return "performance";
  if (value.includes("cro") || value.includes("conversion") || value.includes("conversión")) return "conversion";
  if (value.includes("seo")) return "technical-seo";
  if (value.includes("migration") || value.includes("migrac") || value.includes("replatform")) return "migration";
  if (value.includes("development") || value.includes("desarrollo") || value.includes("liquid") || value.includes("custom")) return "development";
  return "general";
}

function resolveHook(tag: string | null | undefined, locale: Locale): string {
  const t = (tag ?? "").toLowerCase();
  const isEs = locale === "es";

  if (t.includes("seo")) {
    return isEs
      ? "La mayoría de tiendas Shopify pierden revenue por SEO técnico que no pueden ver."
      : "Most Shopify stores leak revenue from technical SEO they can't see.";
  }
  if (t.includes("performance") || t.includes("rendimiento") || t.includes("speed") || t.includes("velocidad")) {
    return isEs
      ? "La mayoría de tiendas Shopify pierden conversiones por una tienda lenta, sin saberlo."
      : "Most Shopify stores lose conversions to a slow store, without knowing it.";
  }
  if (t.includes("cro") || t.includes("conversion") || t.includes("conversión")) {
    return isEs
      ? "La mayoría de tiendas Shopify pierden dinero en un funnel que nunca han auditado."
      : "Most Shopify stores lose money in a funnel they've never audited.";
  }
  if (t.includes("migration") || t.includes("replatform") || t.includes("migrac")) {
    return isEs
      ? "Una migración responsable protege datos, URLs, medición y operación antes del lanzamiento."
      : "A responsible migration protects data, URLs, measurement and operations before launch.";
  }
  if (t.includes("development") || t.includes("dev") || t.includes("liquid") || t.includes("custom") || t.includes("desarrollo")) {
    return isEs
      ? "La ingeniería Shopify responsable empieza por ownership, límites y validación."
      : "Responsible Shopify engineering starts with ownership, boundaries and validation.";
  }

  return isEs
    ? "Una guía práctica para tomar una decisión Shopify con mejor evidencia."
    : "A practical guide for making a better-evidenced Shopify decision.";
}

export function ArticleHeroFrame({ tag, readingMinutes, locale, hookOverride }: ArticleHeroFrameProps) {
  const hook = hookOverride && hookOverride.trim().length > 0 ? hookOverride : resolveHook(tag, locale);
  const minLabel = locale === "es" ? "min de lectura" : "min read";
  const hub = resolveHub(tag);
  const theme = RESOURCE_HUB_THEMES[hub];
  const style = {
    "--hub-accent": theme.accent,
    "--hub-accent-ink": theme.accentInk,
  } as CSSProperties;

  return (
    <div className="article-hero-frame mt-7" data-resource-hub={hub} style={style}>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {tag ? (
          <span
            className="rounded-full border px-3 py-1.5 font-semibold uppercase tracking-[0.16em]"
            style={{
              borderColor: `color-mix(in srgb, ${theme.accent} 46%, transparent)`,
              background: `color-mix(in srgb, ${theme.accent} 10%, #fafaf8)`,
              color: theme.accentInk,
            }}
          >
            {tag}
          </span>
        ) : null}
        <span className="font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {readingMinutes} {minLabel}
        </span>
      </div>
      <p
        className="mt-6 max-w-2xl border-l-2 pl-5 text-base font-medium leading-relaxed text-foreground/85 sm:text-lg"
        style={{ borderColor: theme.accent }}
      >
        {hook}
      </p>
    </div>
  );
}
